import * as React from "react";
import classNames from "classnames";
import Icon from "#components/icon";
import s from "./pagination.module.scss";

export const PAGINATION_DOTS = "dots" as const;

export type PaginationItem = number | typeof PAGINATION_DOTS;

export interface IPaginationProps {
	/** Current page (1-based). */
	page: number;
	/** Total number of pages. */
	totalPages: number;
	/** Called with the target page (1-based) when the user navigates. */
	onChange: (page: number) => void;
	/** How many pages to show on each side of the current page. */
	siblingCount?: number;
	/** How many pages to always show at the start/end. */
	boundaryCount?: number;
	className?: string;
	firstAriaLabel?: string;
	prevAriaLabel?: string;
	nextAriaLabel?: string;
	lastAriaLabel?: string;
}

const range = (start: number, end: number): number[] => {
	const length = Math.max(end - start + 1, 0);
	return Array.from({ length }, (_, index) => start + index);
};

/**
 * Builds the list of page items to render, inserting ellipsis (`PAGINATION_DOTS`)
 * when the page count exceeds what fits around the boundaries and the current page.
 * Mirrors the MUI pagination algorithm.
 */
export function getPaginationItems({
	page,
	totalPages,
	siblingCount = 1,
	boundaryCount = 1,
}: {
	page: number;
	totalPages: number;
	siblingCount?: number;
	boundaryCount?: number;
}): PaginationItem[] {
	if (totalPages <= 0) return [];

	// boundaries + current + siblings on both sides + two ellipsis slots
	const totalNumbers = boundaryCount * 2 + siblingCount * 2 + 3;

	if (totalPages <= totalNumbers) {
		return range(1, totalPages);
	}

	const startPages = range(1, boundaryCount);
	const endPages = range(totalPages - boundaryCount + 1, totalPages);

	const siblingsStart = Math.max(
		Math.min(page - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
		boundaryCount + 2,
	);
	const siblingsEnd = Math.min(
		Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
		endPages.length > 0 ? endPages[0] - 2 : totalPages - 1,
	);

	const items: PaginationItem[] = [...startPages];

	if (siblingsStart > boundaryCount + 2) {
		items.push(PAGINATION_DOTS);
	} else if (boundaryCount + 1 < totalPages - boundaryCount) {
		items.push(boundaryCount + 1);
	}

	items.push(...range(siblingsStart, siblingsEnd));

	if (siblingsEnd < totalPages - boundaryCount - 1) {
		items.push(PAGINATION_DOTS);
	} else if (totalPages - boundaryCount > boundaryCount) {
		items.push(totalPages - boundaryCount);
	}

	items.push(...endPages);

	return items;
}

export function Pagination({
	page,
	totalPages,
	onChange,
	siblingCount = 1,
	boundaryCount = 1,
	className,
	firstAriaLabel = "first",
	prevAriaLabel = "prev",
	nextAriaLabel = "next",
	lastAriaLabel = "last",
}: IPaginationProps) {
	const items = React.useMemo(
		() => getPaginationItems({ page, totalPages, siblingCount, boundaryCount }),
		[page, totalPages, siblingCount, boundaryCount],
	);

	if (totalPages <= 1) {
		return null;
	}

	const goTo = (target: number) => {
		const clamped = Math.min(Math.max(target, 1), totalPages);
		if (clamped !== page) {
			onChange(clamped);
		}
	};

	const isFirst = page <= 1;
	const isLast = page >= totalPages;

	return (
		<nav className={classNames(s.pagination, className)} aria-label="pagination">
			<button
				type="button"
				className={s.navButton}
				aria-label={firstAriaLabel}
				disabled={isFirst}
				onClick={() => goTo(1)}
			>
				<Icon icon="chevron-double-left" />
			</button>
			<button
				type="button"
				className={s.navButton}
				aria-label={prevAriaLabel}
				disabled={isFirst}
				onClick={() => goTo(page - 1)}
			>
				<Icon icon="chevron-right" className={s.flip} />
			</button>

			{items.map((item, index) =>
				item === PAGINATION_DOTS ? (
					<span key={`dots-${index}`} className={s.dots} aria-hidden="true">
						…
					</span>
				) : (
					<button
						type="button"
						key={item}
						className={classNames(s.pageButton, { [s.active]: item === page })}
						aria-label={String(item)}
						aria-current={item === page ? "page" : undefined}
						onClick={() => goTo(item)}
					>
						{item}
					</button>
				),
			)}

			<button
				type="button"
				className={s.navButton}
				aria-label={nextAriaLabel}
				disabled={isLast}
				onClick={() => goTo(page + 1)}
			>
				<Icon icon="chevron-right" />
			</button>
			<button
				type="button"
				className={s.navButton}
				aria-label={lastAriaLabel}
				disabled={isLast}
				onClick={() => goTo(totalPages)}
			>
				<Icon icon="chevron-double-right" />
			</button>
		</nav>
	);
}
